import Box from "@mui/material/Box";

import type { SetupDay } from "@/libs/tournamentSetupApi";

import { addDays, formatDay, formatTime, PickerField } from "./fields";
import { CloseIcon, DenseButton, FieldLabel, tone } from "./ui";

const GRID = "140px minmax(0, 1fr) minmax(0, 1fr) 28px";

/** Play days with first match and last match end (F1). */
export function DaysEditor({
  days,
  onChange,
  trailing,
}: {
  days: SetupDay[];
  onChange: (days: SetupDay[]) => void;
  trailing?: React.ReactNode;
}) {
  const update = (index: number, patch: Partial<SetupDay>) =>
    onChange(
      days
        .map((day, i) => (i === index ? { ...day, ...patch } : day))
        .sort((a, b) => a.play_date.localeCompare(b.play_date)),
    );
  const addDay = () => {
    const last = days[days.length - 1];
    onChange([
      ...days,
      {
        play_date: last ? addDays(last.play_date, 1) : new Date().toISOString().slice(0, 10),
        first_match_at: last?.first_match_at ?? "08:00:00",
        last_match_ends_at: last?.last_match_ends_at ?? "18:00:00",
        court_count: null,
      },
    ]);
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: GRID, gap: 1, alignItems: "center" }}>
        <FieldLabel sx={{ m: 0 }}>Day</FieldLabel>
        <FieldLabel sx={{ m: 0 }}>First match</FieldLabel>
        <FieldLabel sx={{ m: 0 }}>Last match ends</FieldLabel>
        <span />
      </Box>
      {days.map((day, index) => (
        <Box key={`${day.play_date}-${index}`} sx={{ display: "grid", gridTemplateColumns: GRID, gap: 1, alignItems: "center" }}>
          <PickerField
            display={formatDay(day.play_date)}
            label={`Day ${index + 1} date`}
            onChange={(value) => update(index, { play_date: value })}
            type="date"
            value={day.play_date}
          />
          <PickerField
            display={formatTime(day.first_match_at)}
            label={`Day ${index + 1} first match`}
            onChange={(value) => update(index, { first_match_at: value })}
            type="time"
            value={day.first_match_at}
          />
          <PickerField
            display={formatTime(day.last_match_ends_at)}
            label={`Day ${index + 1} last match ends`}
            onChange={(value) => update(index, { last_match_ends_at: value })}
            type="time"
            value={day.last_match_ends_at}
          />
          <Box
            aria-label={`Remove day ${index + 1}`}
            component="button"
            disabled={days.length <= 1}
            onClick={() => onChange(days.filter((_, i) => i !== index))}
            sx={{
              width: 28,
              height: 28,
              border: 0,
              bgcolor: "transparent",
              color: tone.faint,
              borderRadius: "6px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
              cursor: "pointer",
              "&:disabled": { opacity: 0.4, cursor: "default" },
            }}
            type="button"
          >
            <CloseIcon />
          </Box>
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <DenseButton onClick={addDay} size="sm">
          + Add a day
        </DenseButton>
        <Box sx={{ flexGrow: 1 }} />
        {trailing}
      </Box>
    </Box>
  );
}
